import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Dropdown, Input, Modal, Select, Space, Table } from "antd";
import { EllipsisOutlined, SearchOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";
import useUsers from "hooks/userManagement/useUsers";
import useDebounce from "hooks/route-rules/useDebounce";
import { formatDate, STATUS_OPTIONS } from "./userUtils";
import paths from "routes/paths";
import CopyableText from "common/CopyableText";
import IconButton from "common/IconButton";
import SubtleBadge from "common/SubtleBadge";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";

const getBadgeColor = (status = "") => {
  const normalized = String(status || "").toUpperCase();
  if (normalized === "ACTIVE") return "success";
  if (normalized === "PENDING") return "warning";
  if (normalized === "SUSPENDED") return "danger";
  if (normalized === "INACTIVE") return "secondary";
  return "secondary";
};

const UsersPage = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [query, setQuery] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("ALL");
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const {
    users,
    loading,
    error,
    refetch,
    searchUserProfiles,
    deleteUserProfileProperty,
    totalElements,
  } = useUsers(page, pageSize, true, true, { autoFetch: false });

  const debouncedQuery = useDebounce(query, 300);
  const searchActive = debouncedQuery.trim().length >= 3 || selectedStatus !== "ALL";
  const displayedUsers = useMemo(() => {
    const normalizedQuery = debouncedQuery.trim().toLowerCase();

    return users.filter((row) => {
      if (
        selectedStatus !== "ALL" &&
        String(row.status || "").toUpperCase() !== selectedStatus
      ) {
        return false;
      }

      if (!normalizedQuery) {
        return true;
      }

      const haystack = [
        row.id,
        row.firstName,
        row.lastName,
        row.fullName,
        row.email,
        row.phone,
        row.gender,
        row.status,
      ]
        .filter(Boolean)
        .map((value) => String(value).toLowerCase());

      return haystack.some((value) => value.includes(normalizedQuery));
    });
  }, [debouncedQuery, selectedStatus, users]);

  useEffect(() => {
    setPage(0);
  }, [debouncedQuery, selectedStatus]);

  useEffect(() => {
    const load = async () => {
      if (searchActive) {
        await searchUserProfiles(debouncedQuery, selectedStatus, page);
      } else {
        await refetch();
      }
    };

    load();
  }, [debouncedQuery, page, refetch, searchActive, searchUserProfiles, selectedStatus]);

  const handleDelete = async () => {
    if (!deleteTarget?.id) return;
    setSaving(true);
    try {
      await deleteUserProfileProperty(deleteTarget.id);
      toast.success("User deleted successfully");
      setDeleteTarget(null);
      await refetch();
    } catch (deleteError) {
      toast.error(
        deleteError?.response?.data?.message ||
          deleteError?.message ||
          "Failed to delete user",
      );
    } finally {
      setSaving(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        title: "User ID",
        dataIndex: "id",
        key: "id",
        width: 330,
        render: (value, record) => (
          <span
            className="user-id-cell"
            role="button"
            tabIndex={0}
            onClick={(e) => {
              if (e?.target?.closest?.(".ant-typography-copy")) return;
              navigate(`/users/${record.id}`);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") navigate(`/users/${record.id}`);
            }}
            style={{ cursor: "pointer", whiteSpace: "nowrap", fontSize: 12 }}
          >
            <CopyableText value={value} showTooltip={false} stopPropagation={false} />
          </span>
        ),
      },
      {
        title: "Name",
        key: "fullName",
        width: 170,
        render: (_, row) => {
          const fullName = `${row.firstName || ""} ${row.lastName || ""}`.trim();
          return (
            <div
              title={fullName}
              style={{
                maxWidth: 160,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {fullName || "None"}
            </div>
          );
        },
      },
      {
        title: "Email",
        dataIndex: "email",
        key: "email",
        width: 240,
        ellipsis: true,
        render: (v) => v || "None",
      },
      {
        title: "Gender",
        dataIndex: "gender",
        key: "gender",
        width: 110,
        render: (v) => v || "None",
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        align: "center",
        width: 130,
        render: (value) => {
          const status = value || "UNKNOWN";
          return (
            <SubtleBadge
              bg={getBadgeColor(status)}
              className="fw-bold"
              style={{
                borderRadius: 4,
                padding: "2px 6px",
                fontSize: "0.7rem",
                textTransform: "uppercase",
              }}
            >
              {String(status).replaceAll("_", " ")}
            </SubtleBadge>
          );
        },
      },
      {
        title: "Created At",
        dataIndex: "createdAt",
        key: "createdAt",
        align: "center",
        width: 200,
        render: (v) => formatDate(v),
      },
      {
        title: "Actions",
        key: "actions",
        width: 90,
        render: (_, userRow) => (
          <Dropdown
            trigger={["click"]}
            placement="bottomRight"
            menu={{
              items: [
                { key: "view", label: "View Details" },
                { key: "edit", label: "Edit" },
                { type: "divider" },
                { key: "delete", label: <span className="text-danger">Delete</span>, danger: true },
              ],
              onClick: ({ key, domEvent }) => {
                domEvent?.stopPropagation?.();
                if (key === "view") navigate(`/users/${userRow.id}`);
                if (key === "edit") navigate(paths.userEdit(userRow.id));
                if (key === "delete") setDeleteTarget(userRow);
              },
            }}
          >
            <Button
              type="text"
              size="small"
              icon={<EllipsisOutlined />}
              onClick={(e) => e.stopPropagation()}
            />
          </Dropdown>
        ),
      },
    ],
    [navigate],
  );

  const footer = () => (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: 8,
        color: "#64748b",
        fontSize: 13,
      }}
    >
      <span>
        Showing <strong style={{ color: "#111827" }}>{displayedUsers.length}</strong> of{" "}
        <strong style={{ color: "#111827" }}>{totalElements || users.length}</strong> users
      </span>
      <span>
        Page <strong style={{ color: "#111827" }}>{page + 1}</strong>
      </span>
    </div>
  );

  return (
    <div className="project-page">
      <div className="d-flex justify-content-between align-items-center mb-3 mt-3 gap-3 flex-wrap">
        <WidgetSectionTitle
          icon="users"
          title="Members"
          subtitle="Manage all user profiles from one place"
          className="flex-1"
        />
      </div>

      <div className="d-flex justify-content-between align-items-center mb-3 gap-3 flex-wrap">
        <Space wrap>
          <Input
            allowClear
            prefix={<SearchOutlined style={{ color: "#9ca3af" }} />}
            placeholder="Search by ID, name, email or gender"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={{ width: 280, borderRadius: 12 }}
          />
          <Select
            value={selectedStatus}
            onChange={setSelectedStatus}
            options={STATUS_OPTIONS}
            style={{ width: 170 }}
          />
        </Space>

        <IconButton
          variant="success"
          size="sm"
          icon="plus"
          className="text-xs text-white px-3 fw-normal"
          style={{ height: 32 }}
          onClick={() => navigate(paths.userCreate)}
        >
          Add User
        </IconButton>
      </div>

      <div
        style={{
          border: "1px solid #e5e7eb",
          borderRadius: 12,
          background: "#fff",
          overflow: "hidden",
        }}
      >
        <Table
          rowKey="id"
          dataSource={displayedUsers}
          columns={columns}
          size="small"
          bordered
          className="custom-table"
          tableLayout="fixed"
          scroll={{ x: 1200 }}
          loading={loading}
          pagination={{
            current: page + 1,
            pageSize,
            total: totalElements || users.length,
            showSizeChanger: true,
            showQuickJumper: false,
            pageSizeOptions: [10, 20, 30, 50, 100, 200, 500],
            onChange: (nextPage, nextPageSize) => {
              setPage(nextPage - 1);
              setPageSize(nextPageSize);
            },
          }}
          footer={footer}
          onRow={(record) => ({
            onClick: (e) => {
              if (e?.target?.closest?.(".ant-typography-copy")) return;
              navigate(`/users/${record.id}`);
            },
          })}
        />
      </div>

      {error && <div className="alert alert-danger mt-2">{error}</div>}

      <Modal
        open={Boolean(deleteTarget)}
        onCancel={() => setDeleteTarget(null)}
        title="Confirm Delete"
        okText="Delete"
        okType="danger"
        confirmLoading={saving}
        onOk={handleDelete}
        centered
      >
        <p style={{ marginBottom: 0 }}>
          Are you sure you want to delete this user profile? This action cannot be undone.
        </p>
      </Modal>
    </div>
  );
};

export default UsersPage;
